import { LogOut } from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { isOAuthConfigured, logout } from '@/auth/oauth.js';
import { isRunningStandalone } from '@/handshake/mock-host.js';
import { Button } from '@/components/ui/button.js';
import { cn } from '@/lib/utils.js';
import { useHandshakeContext } from './HandshakeContext.js';

const navItems = [
  { to: '/runs', label: 'Runs' },
  { to: '/flows', label: 'Flows' },
  { to: '/settings', label: 'Settings' },
];

export function AppLayout() {
  const { extensionDetails } = useHandshakeContext();
  const navigate = useNavigate();
  const standalone = isRunningStandalone();

  // The app only owns the session in standalone OAuth mode. In integration
  // (iframe) mode the Kadanza host owns it, so we don't surface logout here.
  const canLogout = standalone && isOAuthConfigured();

  return (
    <div className="flex min-h-full flex-col bg-background text-foreground">
      {standalone ? (
        <header className="border-b">
          <div className="mx-auto flex w-full max-w-[1170px] items-center justify-between gap-6 px-6">
            <div className="flex items-center gap-8">
              <button
                type="button"
                onClick={() => navigate('/runs')}
                className="flex items-baseline gap-2 py-3 text-left"
              >
                <span className="text-sm font-semibold tracking-tight">grafx-vdp</span>
                <span className="text-xs text-muted-foreground">
                  Tenant: {extensionDetails?.tenantDomain ?? '—'}
                </span>
              </button>

              <nav className="flex items-center gap-6">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'relative py-4 text-sm font-medium transition-colors',
                        isActive ? 'text-link' : 'text-muted-foreground hover:text-foreground',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {item.label}
                        {isActive ? (
                          <span className="absolute inset-x-0 bottom-0 h-0.5 bg-tab-active" />
                        ) : null}
                      </>
                    )}
                  </NavLink>
                ))}
              </nav>
            </div>

            {canLogout ? (
              <Button variant="ghost" size="sm" onClick={() => logout()}>
                <LogOut />
                Log out
              </Button>
            ) : null}
          </div>
        </header>
      ) : null}

      <main
        className={cn(
          'mx-auto w-full max-w-[1170px] flex-1 px-6',
          standalone ? 'py-8' : 'py-6',
        )}
      >
        <Outlet />
      </main>
    </div>
  );
}
